import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { MessageField, EventInvitationMessage } from 'Src/models/message';
import { FormRendererV2 } from 'Src/components/FormRendererV2/FormRendererV2';
import { FormDefinition } from 'Src/types/formSchema';
import { FormSubmission } from 'Src/types/formSchema';
import {
  applyAnswersToLegacyFields,
  applyLegacyRequiredFlagsToDefinition,
  formDefinitionToLegacyFields,
  legacyFieldsToAnswers,
  legacyFieldsToFormDefinition
} from 'Src/helpers/form/legacyFormAdapter';
import {
  FormAnswerMap,
  FormErrorMap,
  answerMapToArray,
  answersToMap,
  validateFormDefinition
} from 'Src/helpers/form/formRuntime';
import { getSubmissionElementIds, hasFormFlow } from 'Src/helpers/form/formFlowRuntime';
import { uploadFormFilesForElement } from 'Src/helpers/form/formMedia';
import './ParentalConsentRequestForm.scss';
import { AppUser } from 'Src/views/App/App';
import { useSimpleModalRequestClose } from 'Src/components/SimpleModal/SimpleModal';
import { Button } from 'Src/components/Button/Button';

interface Props {
  user: AppUser;
  message: EventInvitationMessage;
  onAcceptClick: (fields: MessageField[], submission?: FormSubmission) => void;
  onCloseClick: () => void;
  onDeclineClick?: () => void;
  submitLabel?: string;
  staffMode?: boolean;
}

interface State {
  definition: FormDefinition;
  answers: FormAnswerMap;
  errors: FormErrorMap;
  consentGiven: boolean;
  consentError: boolean;
}

const ConsentCancelButton = ({ onCloseClick, staffMode }: { onCloseClick: () => void; staffMode?: boolean }) => {
  const requestModalClose = useSimpleModalRequestClose();
  if (staffMode) return <Button text="Cancel" customClass="sit-btn sit-btn--secondary" onClick={requestModalClose || onCloseClick}/>;
  return (
    <button type="button" onClick={requestModalClose || onCloseClick} className="btn-respond btn-close">
      Cancel
    </button>
  );
};

export class ParentalConsentRequestForm extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = this.makeInitialStateFromMessage(props.message);
  }

  private makeInitialStateFromMessage = (message: EventInvitationMessage): State => {
    const fields = (propz.get(message, ['fields'], []) || []).map(field => ({ ...field }));
    const isV2 = message.formSchemaVersion === 2 && !!message.formDefinition;
    let definition = isV2
      ? applyLegacyRequiredFlagsToDefinition(message.formDefinition!, fields)
      : legacyFieldsToFormDefinition(fields);
    if (this.props.staffMode) {
      definition = { ...definition, logic: [], elements: definition.elements
        .filter(element => element.type !== 'PAGE_BREAK')
        .map(element => ({ ...element, required: false, isRequired: false,
          config: { ...element.config, otherRequired: false },
          validation: { ...element.validation, required: false } })) };
    }
    const initialAnswers = isV2 ? message.formAnswers || [] : legacyFieldsToAnswers(fields, definition);
    const answers = answersToMap(initialAnswers);

    return {
      definition,
      answers,
      errors: {},
      consentGiven: !!this.props.staffMode || String(message.invitationStatus || '').toUpperCase() === 'ACCEPTED',
      consentError: false
    };
  };

  componentDidUpdate(prevProps: Props) {
    if (prevProps.message?.id !== this.props.message?.id) {
      this.setState(this.makeInitialStateFromMessage(this.props.message));
    }
  }

  private handleAnswersChange = (answers: FormAnswerMap) => {
    const changedIds = Object.keys(answers).filter(
      id => JSON.stringify(answers[id]) !== JSON.stringify(this.state.answers[id])
    );

    this.setState(prev => {
      const errors = { ...prev.errors };
      changedIds.forEach(id => delete errors[id]);
      return { answers, errors };
    });
  };

  private handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();

    const { definition, answers } = this.state;
    const errors = validateFormDefinition(definition, answers, getSubmissionElementIds(definition, answers));

    if (Object.keys(errors).length > 0 || !this.state.consentGiven) {
      this.setState({ errors, consentError: !this.state.consentGiven });
      return;
    }

    const isV2 = this.props.message.formSchemaVersion === 2 && !!this.props.message.formDefinition;
    const legacyFields = isV2
      ? formDefinitionToLegacyFields(definition)
      : (this.props.message.fields || []).map(field => ({ ...field }));
    const fieldsWithAnswers = applyAnswersToLegacyFields(
      legacyFields,
      definition,
      answerMapToArray(answers)
    ) as MessageField[];

    const submission: FormSubmission | undefined = isV2
      ? {
          formSchemaVersion: 2,
          formAnswers: answerMapToArray(answers)
        }
      : undefined;

    this.props.onAcceptClick(fieldsWithAnswers, submission);
  };

  private handleFormSubmit = (event: React.FormEvent) => {
    if (hasFormFlow(this.state.definition)) {
      event.preventDefault();
      return;
    }
    this.handleSubmit(event);
  };

  render() {
    const { message, onCloseClick, onDeclineClick, submitLabel } = this.props;
    const { definition, answers, errors, consentGiven, consentError } = this.state;
    const flowEnabled = hasFormFlow(definition);
    const formErrors = validateFormDefinition(definition, answers, getSubmissionElementIds(definition, answers));
    const hasRequiredAnswersMissing = Object.keys(formErrors).length > 0;

    const firstName = propz.get(message, ['playerDetailsData', 'firstName'], '');
    const lastName = propz.get(message, ['playerDetailsData', 'lastName'], '');
    const fullName = `${firstName} ${lastName}`.trim();
    const schoolLogo = propz.get(message, ['schoolData', 'pic'], '');
    const initials = (fullName || '•')
      .split(' ')
      .filter(Boolean)
      .map(part => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();

    return (
      <form className="ConsentForm" onSubmit={this.handleFormSubmit}>
        <div className="ConsentForm__header">
          <div className="ConsentForm__avatar" aria-hidden="true">
            {schoolLogo ? <img src={schoolLogo} alt="" /> : initials}
          </div>
          <div className="ConsentForm__titles">
            <h3 className="ConsentForm__title">Consent request</h3>
            <div className="ConsentForm__subtitle">
              {this.props.staffMode ? 'Accept on behalf of the school. All answers are optional. This request will be marked Updated by staff.' : <>
              Do you give your consent for <strong>{fullName || 'the student'}</strong> to take part in this fixture?
              </>}
            </div>
          </div>
        </div>

        {definition.elements.length > 0 ? (
          <div className="ConsentForm__fields">
            <FormRendererV2
              definition={definition}
              answers={answers}
              errors={errors}
              onChange={this.handleAnswersChange}
              onSubmitRequested={() => this.handleSubmit()}
              uploadFiles={(files, element) => uploadFormFilesForElement(this.props.user, files, element)}
            />
          </div>
        ) : (
          <div className="text-muted">No additional information required.</div>
        )}

        {!this.props.staffMode && <label className={consentError ? 'ConsentForm__confirmation is-error' : 'ConsentForm__confirmation'}>
          <input type="checkbox" checked={consentGiven} onChange={event => this.setState({consentGiven:event.target.checked,consentError:false})}/>
          <span>I give consent for {firstName || 'this child'} to take part in this event.</span>
        </label>}
        {consentError ? <div className="ConsentForm__confirmationError">Tick the box to give consent</div> : null}

        <div className="ConsentForm__actionArea">
          {!flowEnabled && hasRequiredAnswersMissing ? (
            <div className="ConsentForm__requiredNotice" role="alert">
              <span aria-hidden="true">!</span>
              <p>Please complete all required fields before continuing.</p>
            </div>
          ) : null}
          <div className={this.props.staffMode ? 'schoolConsentDrawer__actions' : 'ConsentForm__actions'}>
            {onDeclineClick ? (
              <button type="button" onClick={onDeclineClick} className="btn-respond btn-decline">
                Decline
              </button>
            ) : null}
            <ConsentCancelButton onCloseClick={onCloseClick} staffMode={this.props.staffMode} />
            {this.props.staffMode ? <Button type="submit" text={submitLabel || 'Accept consent'} customClass="sit-btn sit-btn--primary"
              disabled={hasRequiredAnswersMissing} onClick={this.handleSubmit}/> : !flowEnabled && (
              <button
                type="submit"
                className="btn-respond btn-accept"
                disabled={hasRequiredAnswersMissing}
                aria-disabled={hasRequiredAnswersMissing}
              >
                {submitLabel || 'Give consent'}
              </button>
            )}
          </div>
        </div>
      </form>
    );
  }
}
